import { jsx as _jsx } from "react/jsx-runtime";
import { Box } from '@chakra-ui/react';
import { $getNodeByKey, DecoratorNode } from 'lexical';
function convertVariableElement(domNode) {
    const variableKey = domNode.getAttribute('data-variable-key');
    if (variableKey) {
        const node = $createVariableNode(variableKey);
        return { node };
    }
    return null;
}
export class VariableNode extends DecoratorNode {
    static getType() {
        return 'variable';
    }
    static clone(node) {
        return new VariableNode(node.__variableKey, node.__key);
    }
    constructor(variableKey, key) {
        super(key);
        this.__variableKey = variableKey;
    }
    createDOM(_config) {
        const element = document.createElement('span');
        element.setAttribute('data-variable-key', this.__variableKey);
        element.setAttribute('data-lexical-variable', 'true');
        element.style.display = 'inline-block';
        element.style.margin = '0';
        element.style.verticalAlign = 'baseline';
        return element;
    }
    updateDOM() {
        return false;
    }
    static importDOM() {
        return {
            span: (domNode) => {
                if (!domNode.hasAttribute('data-lexical-variable')) {
                    return null;
                }
                return {
                    conversion: convertVariableElement,
                    priority: 1,
                };
            },
        };
    }
    static importJSON(serializedNode) {
        const node = $createVariableNode(serializedNode.variableKey);
        return node;
    }
    exportJSON() {
        return {
            type: 'variable',
            version: 1,
            variableKey: this.__variableKey,
        };
    }
    exportDOM() {
        const element = document.createElement('span');
        element.setAttribute('data-variable-key', this.__variableKey);
        element.setAttribute('data-lexical-variable', 'true');
        element.textContent = `{{${this.__variableKey}}}`;
        return { element };
    }
    getTextContent() {
        return `{{${this.__variableKey}}}`;
    }
    decorate(editor) {
        return (_jsx(VariableComponent, { editor: editor, nodeKey: this.getKey(), variableKey: this.__variableKey }));
    }
    isInline() {
        return true;
    }
    isKeyboardSelectable() {
        // Selectable so the caret can land on the chip and Backspace/Delete removes
        // it (previously false, which made the token impossible to delete).
        return true;
    }
}
/**
 * Puts a collapsed caret right before or right after a variable chip. Must run
 * inside an editor update.
 *
 * The chip is a non-editable island, so the browser has no caret position INSIDE
 * it: a click on it either leaves the selection where it was or resolves it to an
 * arbitrary edge (a click on the chip's right half used to land the caret at the
 * start of the line). Selecting the sibling position through Lexical gives the
 * brand a visible caret exactly where the next keystroke will go: the end of the
 * preceding text, the start of the following one, or the paragraph slot beside
 * the chip when it opens or closes the line.
 */
export function $placeCaretBesideVariable(nodeKey, side) {
    const node = $getNodeByKey(nodeKey);
    if (!$isVariableNode(node))
        return false;
    if (side === 'before')
        node.selectPrevious();
    else
        node.selectNext(0, 0);
    return true;
}
function VariableComponent({ editor, nodeKey, variableKey }) {
    const handleMouseDown = (event) => {
        if (event.button !== 0 || !editor.isEditable())
            return;
        // Take over from the browser: its default on a non-editable island either
        // does nothing or drops the caret on the wrong edge.
        event.preventDefault();
        const rect = event.currentTarget.getBoundingClientRect();
        const side = event.clientX < rect.left + rect.width / 2 ? 'before' : 'after';
        // Focus first so the selection written below is the one the DOM keeps.
        editor.getRootElement()?.focus({ preventScroll: true });
        editor.update(() => {
            $placeCaretBesideVariable(nodeKey, side);
        });
    };
    return (_jsx(Box, { as: "span", onMouseDown: handleMouseDown, 
        // The per-recipient blue family: a variable chip IS a per-recipient value
        // (resolved for each recipient at send), same semantics as the brief
        // templates' per-recipient fields.
        bg: "perRecipient.surfaceSubtle", color: "perRecipient.text", px: 1, py: 0, rounded: "xs", fontSize: "inherit", fontWeight: "medium", display: "inline", userSelect: "none", children: `{{${variableKey}}}` }));
}
export function $createVariableNode(variableKey) {
    return new VariableNode(variableKey);
}
export function $isVariableNode(node) {
    return node instanceof VariableNode;
}
